import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useEffect, useMemo, useRef, useState } from 'react'
import Checkbox from 'components/Checkbox/checkbox'
import {
  FilterAllButton,
  FilterNoneButton,
} from 'components/Filters/fields/SelectFilter'
import { FilterButton } from 'components/Filters/components/FilterButton'
import { FilterCloseButton } from 'components/Filters/components/FilterCloseButton'
import {
  EventTypeFilter as EventTypeFilterType,
  useEventHistoryFilterStore,
} from '../EventHistoryFilterStore'

interface EventTypeOption {
  value: EventTypeFilterType
  label: string
}

const ALL_EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  { value: 'deal', label: 'Deals' },
  { value: 'notes', label: 'Notes' },
  { value: 'reminder', label: 'Reminders' },
  { value: 'samples', label: 'Samples' },
  { value: 'email', label: 'Emails' },
  { value: 'contactcompany', label: 'Account Attributes' },
  { value: 'document', label: 'Documents' },
]

interface EventTypeFilterProps {
  /** Subset of event types to show. Defaults to all. */
  availableEventTypes?: EventTypeFilterType[]
}

export function EventTypeFilter({ availableEventTypes }: EventTypeFilterProps) {
  const EVENT_TYPE_OPTIONS = availableEventTypes
    ? ALL_EVENT_TYPE_OPTIONS.filter((o) =>
        availableEventTypes.includes(o.value)
      )
    : ALL_EVENT_TYPE_OPTIONS
  const { pendingEventTypes, setPendingEventTypes } =
    useEventHistoryFilterStore()
  const isActive = !!pendingEventTypes.length

  const [open, setOpen] = useState(false)
  const [internalFilters, setInternalFilters] =
    useState<EventTypeFilterType[]>(pendingEventTypes)

  const activeValueLabel = useMemo(() => {
    const length = pendingEventTypes?.length
    if (!length) return 'All'

    const firstActiveLabel = EVENT_TYPE_OPTIONS.find(
      (o) => o.value === pendingEventTypes[0]
    )
    if (length === 1 && firstActiveLabel) {
      return firstActiveLabel.label
    }

    return `${length} selected`
  }, [pendingEventTypes])

  function handleInternalChange(option: EventTypeOption, selected: boolean) {
    if (selected) {
      setInternalFilters((prev) => prev.filter((f) => f !== option.value))
    } else {
      setInternalFilters((prev) => [...prev, option.value])
    }
  }

  function selectAll() {
    setInternalFilters(EVENT_TYPE_OPTIONS.map((o) => o.value))
  }

  function selectNone() {
    setInternalFilters([])
  }

  function clearFilters() {
    setPendingEventTypes([])
  }

  function saveFilters() {
    setPendingEventTypes(internalFilters)
  }

  // Syncs the internal filters with the filters in the store
  useEffect(() => {
    setInternalFilters(pendingEventTypes)
  }, [pendingEventTypes, open])

  function handleOpenChange(open: boolean) {
    if (!open) {
      // Apply filters when the dropdown closes
      saveFilters()
    }
    setOpen(open)
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title="Event Type"
            onClick={() => setOpen(!open)}
            isActive={isActive}
            activeValueLabel={activeValueLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton isActive={isActive} onClick={() => clearFilters()} />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn('bg-white w-[260px] lg:w-[235px] p-2')}
        sideOffset={5}
      >
        <ItemsList
          options={EVENT_TYPE_OPTIONS}
          onSelect={handleInternalChange}
          selected={internalFilters}
        />
        <hr className={'my-2'} />
        <div className={'w-full'}>
          <div className={'flex items-center justify-between gap-2'}>
            <FilterAllButton
              isSelected={internalFilters.length === EVENT_TYPE_OPTIONS.length}
              onClick={() => selectAll()}
            />
            <FilterNoneButton
              isSelected={internalFilters.length === 0}
              onClick={() => selectNone()}
            />
          </div>

          <FbButton
            size={'sm'}
            className={'w-full mt-2'}
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: EventTypeOption[]
  selected: EventTypeFilterType[]
  onSelect: (option: EventTypeOption, selected: boolean) => void
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          contain: 'layout style paint',
          willChange: 'auto',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected.some((s) => s === o?.value)
          if (!o) return null
          return (
            <div
              key={item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-3 gap-2 hover:bg-gray-100 text-sm overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              onClick={(e) => {
                e.preventDefault()
                props.onSelect(o, isSelected)
              }}
            >
              <Checkbox
                checked={isSelected}
                readOnly
                wrapperClassName="flex-shrink-0"
              />
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
